import * as React from 'react';
import { OutstandingFeesBreakdown } from 'services/clients/insights';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { Field, GraphTooltip } from 'pages/Shipments/HistoricalPerDiem/components/GraphTooltip';
import { LabelFormatter, AnnotatedRow } from './types';
import { Bar } from './Bar';

interface Props {
  row: AnnotatedRow;
  labelFormatter?: LabelFormatter;
  view: OutstandingFeesBreakdown;
  showTooltips?: boolean;
}

const TooltipWrapper: React.FC<{
  children: React.ReactElement;
  row: AnnotatedRow;
  showTooltips?: boolean;
}> = (props) => {
  const { children, row, showTooltips } = props;

  const fields: Field[] = React.useMemo(() => {
    let tempFields: Field[] = [];
    row.data.forEach((b) => {
      tempFields = [
        ...tempFields,
        {
          field: `${b.detail}_${b.variant}_disputable`,
          variant: 'disputable',
          isPast: b.variant === 'historic',
          value: b.blueAmount,
        },
        {
          field: `${b.detail}_${b.variant}_non-disputable`,
          variant: 'non-disputable',
          isPast: b.variant === 'historic',
          value: b.redAmount,
        },
      ];
    });
    return tempFields;
  }, [row]);

  if (!showTooltips) return children;

  return (
    <Tooltip title={<GraphTooltip fields={fields} />} classes={{ tooltip: 'bg-none p-0' }}>
      {children}
    </Tooltip>
  );
};

export const Group: React.FC<Props> = (props) => {
  const { row, labelFormatter, view, showTooltips } = props;

  let detailColWidth = '180px';
  if (view === OutstandingFeesBreakdown.Container) {
    detailColWidth = '70px';
  }

  return (
    <TooltipWrapper showTooltips={showTooltips} row={row}>
      <div
        className={classNames(
          'grid space-x-2 items-center font-medium text-gray-500 transition-all py-1',
          { 'hover:bg-blue-200': showTooltips }
        )}
        style={{ gridTemplateColumns: '96px auto' }}
      >
        <h5 className="truncate text-left text-xs shrink-0">
          {labelFormatter ? labelFormatter(row.label, row.key, view) : row.label}
        </h5>
        <div className="space-y-2 h-full">
          {row.bars.map((bar, i) => {
            return (
              <div
                className="grid items-center"
                style={{ gridTemplateColumns: `auto ${detailColWidth}` }}
                /* eslint-disable-next-line react/no-array-index-key */
                key={`${row.key}_${i}`}
              >
                <Bar {...bar} />
                <h5 className="truncate text-right text-xs">{bar.detail}</h5>
              </div>
            );
          })}
        </div>
      </div>
    </TooltipWrapper>
  );
};
